// src/features/admin/orders/StatusUpdateForm.tsx
import React, { useEffect, useMemo, useState } from "react";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import { getErrorMessage } from "utils/errors";
import type { AdminOrderOut, AdminPatchStatusIn, OrderStatus, LocationType } from "api/adminOrders";

interface Props {
  order: AdminOrderOut;
  onSubmit: (payload: AdminPatchStatusIn) => Promise<void>;
}

const STATUS_OPTIONS: OrderStatus[] = [
  "pending",
  "paid",
  "booked",
  "collected",
  "processing",
  "resulted",
  "refunded",
  "cancelled",
];

function toDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function datetimeLocalToIso(v: string): string | null {
  const t = v.trim();
  if (!t) return null;
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return t; // let backend decide if it supports raw
  return d.toISOString();
}

const StatusUpdateForm: React.FC<Props> = ({ order, onSubmit }) => {
  const [status, setStatus] = useState<OrderStatus>(order.status);
  const [locationType, setLocationType] = useState<LocationType>(order.location_type ?? null);
  const [appointmentAt, setAppointmentAt] = useState<string>(
    toDatetimeLocalValue(order.appointment_at ?? null)
  );

  const [note, setNote] = useState<string>(order.note ?? "");

  const [mobileLine1, setMobileLine1] = useState(order.mobile_address_line1 ?? "");
  const [mobileLine2, setMobileLine2] = useState(order.mobile_address_line2 ?? "");
  const [mobileCity, setMobileCity] = useState(order.mobile_city ?? "");
  const [mobilePostcode, setMobilePostcode] = useState(order.mobile_postcode ?? "");

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setStatus(order.status);
    setLocationType(order.location_type ?? null);
    setAppointmentAt(toDatetimeLocalValue(order.appointment_at ?? null));
    setNote(order.note ?? "");
    setMobileLine1(order.mobile_address_line1 ?? "");
    setMobileLine2(order.mobile_address_line2 ?? "");
    setMobileCity(order.mobile_city ?? "");
    setMobilePostcode(order.mobile_postcode ?? "");
  }, [order.id, order.lock_version]);

  const showMobile = useMemo(() => locationType === "mobile", [locationType]);

  async function handleSave() {
    setErr("");
    setSaving(true);
    try {
      const payload: AdminPatchStatusIn = {
        expected_lock_version: order.lock_version,
        status,
        location_type: locationType,
        appointment_at: datetimeLocalToIso(appointmentAt),

        note: note.trim() ? note : null,

        mobile_address_line1: showMobile && mobileLine1.trim() ? mobileLine1.trim() : null,
        mobile_address_line2: showMobile && mobileLine2.trim() ? mobileLine2.trim() : null,
        mobile_city: showMobile && mobileCity.trim() ? mobileCity.trim() : null,
        mobile_postcode: showMobile && mobilePostcode.trim() ? mobilePostcode.trim() : null,
      };

      await onSubmit(payload);
    } catch (e: any) {
      setErr(getErrorMessage(e, "Failed to update order"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="border rounded-2xl p-4 space-y-4">
      <h2 className="text-xl font-semibold">Order status</h2>

      {err && (
        <div className="border border-red-300 bg-red-50 text-red-700 p-2 rounded">
          {err}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300"
            value={status}
            onChange={(e) => setStatus(e.target.value as OrderStatus)}
            disabled={saving}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Location type</label>
          <select
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300"
            value={locationType ?? ""}
            onChange={(e) => setLocationType((e.target.value || null) as LocationType)}
            disabled={saving}
          >
            <option value="">—</option>
            <option value="clinic">clinic</option>
            <option value="mobile">mobile</option>
          </select>
        </div>

        <Input
          id="appointment_at"
          label="Appointment time"
          type="datetime-local"
          value={appointmentAt}
          onChange={(e) => setAppointmentAt(e.target.value)}
          disabled={saving}
        />

        <div className="sm:col-span-2">
          <label className="block text-sm font-medium mb-1">Internal note</label>
          <textarea
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={saving}
            placeholder="Internal note visible to admins only"
          />
        </div>

        {showMobile && (
          <>
            <Input
              id="mobile_line1"
              label="Mobile address line 1"
              value={mobileLine1}
              onChange={(e) => setMobileLine1(e.target.value)}
              disabled={saving}
            />
            <Input
              id="mobile_line2"
              label="Mobile address line 2"
              value={mobileLine2}
              onChange={(e) => setMobileLine2(e.target.value)}
              disabled={saving}
            />
            <Input
              id="mobile_city"
              label="Mobile city"
              value={mobileCity}
              onChange={(e) => setMobileCity(e.target.value)}
              disabled={saving}
            />
            <Input
              id="mobile_postcode"
              label="Mobile postcode"
              value={mobilePostcode}
              onChange={(e) => setMobilePostcode(e.target.value)}
              disabled={saving}
            />
          </>
        )}
      </div>

      <div className="flex gap-2">
        <Button type="button" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </section>
  );
};

export default StatusUpdateForm;
